import React from "react";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import ImageGallery from "../components/common/ImageGallery";

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1759310610325-2c7cb621e5e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const About = () => {
    const tr = useT();
    const { lang } = useLang();

    return (
        <div>
            {/* Page header */}
            <section className="bg-gradient-to-br from-slate-900 to-red-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-3">
                        {tr(t.org.regNo)}
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        {tr(t.about.title)}
                    </h1>
                    <p className="mt-3 text-slate-300 max-w-2xl">{tr(t.org.nameFull)}</p>
                </div>
            </section>

            {/* Intro */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                            {lang === "mr" ? "आमची ओळख" : "Who We Are"}
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-5">
                            {lang === "mr"
                                ? "सभासद-केंद्रित सहकारी पतपेढी"
                                : "A member-centric cooperative society"}
                        </h2>
                        <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
                            {tr(t.about.intro)}
                        </p>
                    </div>
                    <div className="relative">
                        <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-200 rounded-2xl -z-10" />
                        <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-red-200 rounded-2xl -z-10" />
                        <img
                            src={ABOUT_IMG}
                            alt="Trust & Partnership"
                            className="w-full rounded-2xl shadow-xl object-cover aspect-[5/4]"
                        />
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl p-8 border-l-4 border-red-700 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 rounded-lg bg-red-50 text-red-700 flex items-center justify-center mb-4">
                            <i className="fa-solid fa-bullseye text-xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{tr(t.about.mission)}</h3>
                        <p className="text-slate-700 leading-relaxed">{tr(t.about.missionText)}</p>
                    </div>
                    <div className="bg-white rounded-2xl p-8 border-l-4 border-amber-600 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-4">
                            <i className="fa-solid fa-eye text-xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{tr(t.about.vision)}</h3>
                        <p className="text-slate-700 leading-relaxed">{tr(t.about.visionText)}</p>
                    </div>
                </div>
            </section>

            {/* Key Features */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                        {lang === "mr" ? "वैशिष्ट्ये" : "What Makes Us Different"}
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                        {tr(t.about.featuresTitle)}
                    </h2>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {t.about.features.map((f, i) => (
                        <div
                            key={i}
                            className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:border-amber-500/50 hover:shadow-md transition-all"
                        >
                            <div className="shrink-0 w-9 h-9 rounded-full bg-green-50 text-green-700 flex items-center justify-center">
                                <i className="fa-solid fa-check text-base" />
                            </div>
                            <div className="font-semibold text-slate-800 text-[15px] leading-snug">
                                {tr(f)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Board of Members */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="text-center max-w-3xl mx-auto mb-10">
                        <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                            {lang === "mr" ? "आमचे नेतृत्व" : "Our Leadership"}
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                            {tr(t.about.boardTitle)}
                        </h2>
                        <p className="mt-3 text-slate-600">{tr(t.about.boardSubtitle)}</p>
                    </div>

                    <ImageGallery
                        folder="board"
                        prefix="board-"
                        max={30}
                        altPrefix={tr(t.about.boardTitle)}
                        gridClassName="grid-cols-2 sm:grid-cols-3 lg:grid-cols-3"
                        imageClassName="h-72 sm:h-80 lg:h-96"
                        fitClassName="object-contain"
                        emptyMessage={
                            lang === "mr"
                                ? "संचालक मंडळाची छायाचित्रे लवकरच उपलब्ध होतील."
                                : "Board member photos will be added soon."
                        }
                    />
                </div>
            </section>

            {/* Achievements */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                        {lang === "mr" ? "गौरव" : "Milestones"}
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                        {tr(t.about.achievementsTitle)}
                    </h2>
                    <p className="mt-3 text-slate-600">{tr(t.about.achievementsSubtitle)}</p>
                </div>

                <ImageGallery
                    folder="achievements"
                    prefix="achievement-"
                    max={30}
                    altPrefix={tr(t.about.achievementsTitle)}
                    gridClassName="grid-cols-1 sm:grid-cols-2"
                    imageClassName="h-80 sm:h-96 lg:h-[28rem]"
                    fitClassName="object-contain"
                    emptyMessage={
                        lang === "mr"
                            ? "यशोगाथेची छायाचित्रे लवकरच उपलब्ध होतील."
                            : "Achievement photos will be added soon."
                    }
                />
            </section>
        </div>
    );
};

export default About;
